// «Эфир»: живая лента действий и переписки агентов + кто что сейчас меняет + изменения в репозиториях.
import { useMemo, useState } from 'react'
import { api, qs } from '../api.ts'
import { ActorLabel } from '../components/AgentAvatar.tsx'
import { AgentSelect } from '../components/AgentSelect.tsx'
import { MessageItem } from '../components/MessageThread.tsx'
import { Badge, Button, Empty, ErrorBox, Loading, PageHeader, Section } from '../components/ui.tsx'
import { useAsync, useBusy } from '../lib/hooks.ts'
import { CHANGE_SOURCE_LABELS, MESSAGE_KIND_LABELS, formatDateTime, timeAgo } from '../lib/format.ts'
import { TaskLink } from '../router.tsx'
import { useAgentMap, useLive, useNow, useReconnect, useStream } from '../store.ts'
import { toastError } from '../toast.ts'
import type { Activity, ChangeEvent, Intent, Message, MessageKind } from '../types.ts'
import { IntentRow } from './TaskDrawer.tsx'

type Item = { kind: 'activity'; at: string; key: string; a: Activity } | { kind: 'message'; at: string; key: string; m: Message }
type KindFilter = 'all' | 'messages' | 'activity' | MessageKind

const PAGE = 80

function mergeById<T extends { id: number }>(cur: T[], add: T[]): T[] {
  const seen = new Set(cur.map((x) => x.id))
  return [...cur, ...add.filter((x) => !seen.has(x.id))]
}

export function FeedPage() {
  const agents = useAgentMap()
  const directions = useLive((s) => s.directions)
  const now = useNow()
  const [dir, setDir] = useState('')
  const [agent, setAgent] = useState('')
  const [kind, setKind] = useState<KindFilter>('all')
  const [exhausted, setExhausted] = useState({ activity: false, messages: false })
  const [loadingMore, runMore] = useBusy()

  const feed = useAsync(async (signal) => {
    const [activity, messages] = await Promise.all([
      api.get<Activity[]>(`/api/activity${qs({ limit: PAGE })}`, { signal }),
      api.get<Message[]>(`/api/messages${qs({ limit: PAGE })}`, { signal }),
    ])
    return { activity, messages }
  }, [])
  const intents = useAsync((signal) => api.get<Intent[]>('/api/intents?status=active', { signal }), [])
  const changes = useAsync((signal) => api.get<ChangeEvent[]>(`/api/changes${qs({ limit: 40 })}`, { signal }), [])

  useStream((ev) => {
    if (ev.type === 'activity') {
      feed.setData((cur) => (cur && !cur.activity.some((x) => x.id === ev.data.id) ? { ...cur, activity: [ev.data, ...cur.activity] } : cur))
    } else if (ev.type === 'message') {
      feed.setData((cur) => (cur && !cur.messages.some((x) => x.id === ev.data.id) ? { ...cur, messages: [ev.data, ...cur.messages] } : cur))
    } else if (ev.type === 'intent') {
      intents.setData((cur) => {
        if (!cur) return cur
        const rest = cur.filter((x) => x.id !== ev.data.id)
        return ev.data.status === 'active' ? [ev.data, ...rest] : rest
      })
    } else if (ev.type === 'change') {
      changes.setData((cur) => (cur && !cur.some((x) => x.id === ev.data.id) ? [ev.data, ...cur] : cur))
    }
  })
  useReconnect(() => {
    feed.reload()
    intents.reload()
    changes.reload()
  })

  const loadMore = () =>
    runMore(async () => {
      const cur = feed.data
      if (!cur) return
      try {
        const minA = cur.activity.reduce((m, x) => Math.min(m, x.id), Number.MAX_SAFE_INTEGER)
        const minM = cur.messages.reduce((m, x) => Math.min(m, x.id), Number.MAX_SAFE_INTEGER)
        const [a, m] = await Promise.all([
          exhausted.activity || !cur.activity.length ? Promise.resolve([] as Activity[]) : api.get<Activity[]>(`/api/activity${qs({ limit: PAGE, before: minA })}`),
          exhausted.messages || !cur.messages.length ? Promise.resolve([] as Message[]) : api.get<Message[]>(`/api/messages${qs({ limit: PAGE, before: minM })}`),
        ])
        setExhausted((x) => ({ activity: x.activity || a.length < PAGE, messages: x.messages || m.length < PAGE }))
        feed.setData((c) => (c ? { activity: mergeById(c.activity, a), messages: mergeById(c.messages, m) } : c))
      } catch (e) {
        toastError(e, 'Не удалось загрузить ещё')
      }
    })

  const items = useMemo(() => {
    if (!feed.data) return []
    const agentDir = (id: string | null | undefined) => (id ? agents.get(id)?.direction_id ?? null : null)
    const out: Item[] = []
    if (kind === 'all' || kind === 'activity') {
      for (const a of feed.data.activity) {
        if (agent && a.actor_id !== agent && a.target_id !== agent) continue
        if (dir && !(a.actor_type === 'agent' && agentDir(a.actor_id) === dir)) continue
        out.push({ kind: 'activity', at: a.created_at, key: `a${a.id}`, a })
      }
    }
    if (kind !== 'activity') {
      for (const m of feed.data.messages) {
        if (kind !== 'all' && kind !== 'messages' && m.kind !== kind) continue
        if (agent && m.from_id !== agent && m.to_id !== agent) continue
        if (
          dir &&
          !(
            (m.from_type === 'agent' && agentDir(m.from_id) === dir) ||
            (m.to_type === 'agent' && agentDir(m.to_id) === dir) ||
            (m.to_type === 'direction' && m.to_id === dir)
          )
        )
          continue
        out.push({ kind: 'message', at: m.created_at, key: `m${m.id}`, m })
      }
    }
    out.sort((x, y) => y.at.localeCompare(x.at))
    return out
  }, [feed.data, kind, agent, dir, agents])

  const canLoadMore = !exhausted.activity || !exhausted.messages

  return (
    <div className="page">
      <PageHeader title="Эфир" lede="Всё, что агенты делают и говорят друг другу — в реальном времени." className="mb-4" />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {/* на телефоне фильтры растягиваются по ширине: по два в строке, без выхода за край */}
        <div className="flex flex-wrap items-center gap-2 max-sm:w-full">
          <select className="ctl h-8 w-auto max-w-full max-sm:min-w-[9rem] max-sm:flex-1" value={dir} onChange={(e) => setDir(e.target.value)} aria-label="Направление">
            <option value="">Все направления</option>
            {directions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <AgentSelect
            value={agent}
            onChange={setAgent}
            emptyLabel="Все агенты"
            ariaLabel="Агент"
            className="h-8 w-auto max-w-[220px] max-sm:max-w-full max-sm:min-w-[9rem] max-sm:flex-1"
          />
          <select className="ctl h-8 w-auto max-w-full max-sm:min-w-[9rem] max-sm:flex-1" value={kind} onChange={(e) => setKind(e.target.value as KindFilter)} aria-label="Тип событий">
            <option value="all">Всё</option>
            <option value="messages">Только сообщения</option>
            <option value="activity">Только действия</option>
            <optgroup label="Сообщения по типу">
              {(Object.keys(MESSAGE_KIND_LABELS) as MessageKind[]).map((k) => (
                <option key={k} value={k}>
                  {MESSAGE_KIND_LABELS[k]}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-w-0">
          {feed.error && !feed.data ? (
            <ErrorBox error={feed.error} onRetry={feed.reload} />
          ) : !feed.data ? (
            <Loading />
          ) : items.length === 0 ? (
            <Empty icon="message">В эфире тихо{dir || agent || kind !== 'all' ? ' — попробуй снять фильтры' : ''}</Empty>
          ) : (
            <ol className="flex flex-col gap-1.5" aria-label="Лента событий">
              {items.map((it) =>
                it.kind === 'message' ? (
                  <li key={it.key}>
                    <MessageItem m={it.m} showTask />
                  </li>
                ) : (
                  // на телефоне: кто и когда — первой строкой, что сделал — второй, во всю ширину
                  <li key={it.key} className="flex items-start gap-2.5 rounded-card px-3.5 py-2 text-[13px] hover:bg-surface max-sm:flex-wrap max-sm:gap-y-1">
                    <span className="mt-px shrink-0 max-sm:min-w-0 max-sm:shrink">
                      <ActorLabel type={it.a.actor_type} id={it.a.actor_id} size="xs" link />
                    </span>
                    <span className="min-w-0 flex-1 text-muted [overflow-wrap:anywhere] max-sm:order-last max-sm:basis-full">
                      <span className="text-fg">{it.a.summary || it.a.action}</span>
                      {it.a.target_type === 'task' && it.a.target_id ? (
                        <>
                          {' '}
                          <TaskLink id={Number(it.a.target_id)} className="font-mono text-[11.5px] hover:text-fg hover:underline">
                            OMA-{it.a.target_id}
                          </TaskLink>
                        </>
                      ) : null}
                    </span>
                    <span className="hidden shrink-0 font-mono text-[10.5px] text-subtle sm:inline">{it.a.action}</span>
                    <time className="tnum shrink-0 text-[11px] text-subtle max-sm:ml-auto" title={formatDateTime(it.a.created_at)}>
                      {timeAgo(it.a.created_at, now)}
                    </time>
                  </li>
                ),
              )}
            </ol>
          )}
          {feed.data && canLoadMore && items.length > 0 ? (
            <div className="mt-3 flex justify-center">
              <Button onClick={() => void loadMore()} loading={loadingMore}>
                Показать раньше
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Section
            title={
              <span className="inline-flex items-center gap-2">
                Кто что сейчас меняет
                {intents.data?.length ? <Badge>{intents.data.length}</Badge> : null}
              </span>
            }
            bodyClassName="p-3"
          >
            {intents.error && !intents.data ? (
              <ErrorBox error={intents.error} onRetry={intents.reload} />
            ) : !intents.data ? (
              <Loading />
            ) : intents.data.length === 0 ? (
              <p className="py-2 text-center text-[12.5px] text-muted">Никто ничего не меняет</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {intents.data.map((i) => (
                  <IntentRow key={i.id} it={i} showTask />
                ))}
              </ul>
            )}
          </Section>

          <Section title="Изменения в репозиториях" bodyClassName="p-0">
            {changes.error && !changes.data ? (
              <div className="p-3">
                <ErrorBox error={changes.error} onRetry={changes.reload} />
              </div>
            ) : !changes.data ? (
              <Loading />
            ) : changes.data.length === 0 ? (
              <p className="px-3 py-4 text-center text-[12.5px] text-muted">Изменений пока нет</p>
            ) : (
              <ul className="divide-y divide-line">
                {changes.data.map((c) => (
                  <li key={c.id} className="px-[18px] py-2.5 text-[13px]">
                    <div className="flex items-start gap-2">
                      <Badge tone={c.source === 'deploy' ? 'warn' : 'neutral'}>
                        {CHANGE_SOURCE_LABELS[c.source] ?? c.source}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        {c.url ? (
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="line-clamp-2 break-words hover:underline">
                            {c.title}
                          </a>
                        ) : (
                          <span className="line-clamp-2 break-words">{c.title}</span>
                        )}
                        <div className="mt-0.5 truncate text-[11.5px] text-subtle">
                          <span className="font-mono">
                            {c.repo ?? c.service_id ?? '—'}
                            {c.ref ? `@${/^[0-9a-f]{12,}$/i.test(c.ref) ? c.ref.slice(0, 7) : c.ref}` : ''}
                          </span>
                          {c.author ? ` · ${c.author}` : ''}
                        </div>
                      </div>
                      <time className="tnum shrink-0 text-[11px] text-subtle" title={formatDateTime(c.happened_at)}>
                        {timeAgo(c.happened_at, now)}
                      </time>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  )
}
